// glonet-react-tailwind-project--src--components--HostRequestDialog.js
// The /hosts "Request host access" window. Signed-in members only (the page
// checks before opening). The voice agent starts IMMEDIATELY (no "dictate"
// button). The plan card here is EDITABLE — the same state as the page
// calculator — because two of the three "Request host access" buttons are
// above the calculator and most visitors arrive without having touched it.
// planTouched decides the agent's opening move: touched -> confirm the plan
// the member built; untouched -> the agent leads plan-building, writing
// answers onto the card through the set_plan client tool (the card must
// follow the conversation, not trail it). Hand edits mid-conversation reach
// the agent as a silent contextual update. The silent-mic rescue rides along
// exactly as on the landing intake; a typed path stays behind a link.
import React, { useEffect, useRef, useState } from "react";
import { Conversation } from "@elevenlabs/client";
import api from "../services/api";
import { GLONET_HOST_INTAKE_AGENT_ID } from "../config/glonetai";
import MicSelector, { GA_MIC_DEVICE_KEY } from "./MicSelector";
import { computeSplit } from "./hostSplit";
import log from "../utils/logger";
import {
  gvTel, gvTelReset, gvMark, gvTimeSpan, gvTimedTools, gvModeRecorder,
} from "../utils/voiceTelemetry";

const ACCENT = "#74a0f5";
const MONO = "'JetBrains Mono',monospace";

// One sentence describing the plan — must describe EXACTLY what the card shows.
export function planBrief(cfg, calc) {
  const feat = `Water Cooler ${cfg.wc ? "on" : "off"}, audio pre-meetings ${cfg.convos ? "on" : "off"}`;
  if (cfg.model === "one_time") {
    return `one-time tickets at $${calc.p} for about ${Number(cfg.members).toLocaleString()} members, ${feat} — you keep ${calc.hostPct}% of every ticket`;
  }
  if (!calc.viable) {
    return `a $${calc.p} per month subscription with ${feat} — which doesn't cover platform costs; the minimum workable price is $${calc.minPrice} per month`;
  }
  return `a $${calc.p} per month subscription for about ${Number(cfg.members).toLocaleString()} members, ${feat} — you keep ${calc.hostPct}%` +
    (calc.hostMonthly ? `, roughly $${Math.round(calc.hostMonthly).toLocaleString()} a month` : "");
}

function planPayload(cfg, calc, confirmed, notes) {
  return {
    model: cfg.model, price: calc.p, members: Number(cfg.members) || 0,
    water_cooler: !!cfg.wc, pre_meetings: !!cfg.convos,
    viable: !!calc.viable, min_price: calc.minPrice,
    host_pct: calc.hostPct, platform_pct: calc.platformPct,
    host_monthly: calc.hostMonthly, base_bucket: calc.baseBucket,
    brand_bucket: calc.brandBucket, floor_binds: !!calc.floorBinds,
    plan_confirmed: !!confirmed, plan_notes: (notes || "").slice(0, 800),
  };
}

const toBool = (v) => (v === true || v === "true" ? true : v === false || v === "false" ? false : null);

export default function HostRequestDialog({ onClose, onSent, cfg, setCfg, inputs, planTouched, micSilenceSecs = 5 }) {
  const [phase, setPhase] = useState("starting"); // starting | live | typing | sending | sent | error
  const [errMsg, setErrMsg] = useState("");
  const [note, setNote] = useState("");
  const [speaking, setSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [heardTick, setHeardTick] = useState(0);
  const sessionRef = useRef(null);
  const phaseRef = useRef("starting"); phaseRef.current = phase;
  const cfgRef = useRef(cfg); cfgRef.current = cfg;
  const inputsRef = useRef(inputs); inputsRef.current = inputs;
  const fromToolRef = useRef(false);   // last cfg change came from set_plan — agent already knows
  const liveBriefRef = useRef(null);   // the plan brief the agent last heard (null until live)

  const calc = computeSplit(inputs, cfg);

  const endVoice = async () => {
    const s = sessionRef.current;
    sessionRef.current = null;
    if (s) { try { await s.endSession(); } catch (e) { /* already closed */ } }
  };

  const submit = async (text, source, confirmed, notes) => {
    if (phaseRef.current === "sending" || phaseRef.current === "sent") return;
    setPhase("sending");
    try {
      const cfgNow = cfgRef.current;
      const calcNow = computeSplit(inputsRef.current, cfgNow);
      await api.post("subscriptions/host-request", {
        note: (text || "").trim().slice(0, 4000),
        source,
        plan: planPayload(cfgNow, calcNow, confirmed, notes),
      });
      setPhase("sent");
      await endVoice();
      if (onSent) onSent();
    } catch (e) {
      log.error("host request failed", { error: e });
      setErrMsg("Couldn't send the request — try again in a moment.");
      setPhase("error");
    }
  };

  // Voice starts as soon as the window opens — this IS the request flow.
  useEffect(() => {
    let cancelled = false;
    gvTelReset("host intake");
    (async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        gvMark("mic_granted");
        // Bluetooth headsets swap profiles when the mic engages and swallow
        // the greeting without this pause (same fix as VoiceOnboarding).
        await new Promise((r) => setTimeout(r, 1200));
        if (cancelled) return;
        // saved mic from the silent-mic selector, self-healing when stale
        let mic = localStorage.getItem(GA_MIC_DEVICE_KEY) || "";
        if (mic) {
          try {
            const devs = await navigator.mediaDevices.enumerateDevices();
            if (!devs.some((d) => d.kind === "audioinput" && d.deviceId === mic)) {
              localStorage.removeItem(GA_MIC_DEVICE_KEY);
              mic = "";
            }
          } catch (e) { /* enumeration unavailable — try it anyway */ }
        }
        const cfgNow = cfgRef.current;
        const calcNow = computeSplit(inputsRef.current, cfgNow);
        const brief = planBrief(cfgNow, calcNow);
        const firstLine = planTouched
          ? `Happy to get you set up as a host. First, the plan you put together on the page: ${brief}. Is that what you intend?`
          : "Happy to get you set up as a host. Let's sketch your plan first — would you charge a monthly subscription, or sell one-time event tickets?";
        const recordMode = gvModeRecorder("host-intake");
        const session = await gvTimeSpan("session_connect", () => Conversation.startSession({
          agentId: GLONET_HOST_INTAKE_AGENT_ID,
          connectionType: "websocket",
          ...(mic ? { inputDeviceId: mic } : {}),
          dynamicVariables: {
            plan_brief: brief,
            plan_viable: calcNow.viable ? "true" : "false",
            plan_min_price: String(calcNow.minPrice || ""),
            plan_touched: planTouched ? "true" : "false",
            first_line: firstLine,
          },
          clientTools: gvTimedTools("host-intake", {
            set_plan: async (args) => {
              const a = args || {};
              const patch = {};
              if (a.model === "subscription" || a.model === "one_time") patch.model = a.model;
              if (a.price != null && Number.isFinite(Number(a.price))) {
                patch.price = Math.min(5000, Math.max(1, Number(a.price)));
              }
              if (a.members != null && Number.isFinite(Number(a.members))) {
                patch.members = Math.min(1000000, Math.max(10, Math.round(Number(a.members))));
              }
              const wcB = toBool(a.water_cooler);
              if (wcB !== null) patch.wc = wcB;
              const pmB = toBool(a.pre_meetings);
              if (pmB !== null) patch.convos = pmB;
              const next = { ...cfgRef.current, ...patch };
              fromToolRef.current = true;
              setCfg(() => next);
              const nextCalc = computeSplit(inputsRef.current, next);
              return `Plan card updated. It now reads: ${planBrief(next, nextCalc)}.`;
            },
            submit_host_request: async (args) => {
              const summary = (args && args.summary) || "";
              setNote(summary);
              await submit(summary, "voice",
                args && args.plan_confirmed, args && args.plan_notes);
            },
          }),
          onModeChange: ({ mode }) => {
            recordMode(mode);
            setSpeaking(mode === "speaking");
            if (mode === "speaking") setThinking(false);
          },
          onMessage: ({ source }) => {
            gvTel("message", { surface: "host-intake", source });
            if (source === "user") { setThinking(true); setHeardTick(Date.now()); }
            else setThinking(false);
          },
          onDisconnect: () => {
            sessionRef.current = null;
            setPhase((prev) => (prev === "live" || prev === "starting" ? "typing" : prev));
          },
        }));
        if (cancelled) { try { await session.endSession(); } catch (e) { /* closing */ } return; }
        sessionRef.current = session;
        gvMark("session_live");
        setPhase("live");
      } catch (e) {
        log.error("host intake voice failed", { error: e });
        if (!cancelled) {
          setErrMsg("Microphone unavailable — type it below instead.");
          setPhase("typing");
        }
      }
    })();
    return () => { cancelled = true; endVoice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hand edits to the plan card mid-conversation reach the agent silently
  // (dynamic variables are read once at session mint; without this the agent
  // confirms a stale plan). set_plan changes skip it — the tool result
  // already told the agent.
  useEffect(() => {
    if (phase !== "live") return undefined;
    const brief = planBrief(cfg, computeSplit(inputs, cfg));
    if (liveBriefRef.current === null || fromToolRef.current) {
      fromToolRef.current = false;
      liveBriefRef.current = brief;
      return undefined;
    }
    if (brief === liveBriefRef.current) return undefined;
    const t = setTimeout(() => {
      liveBriefRef.current = brief;
      const s = sessionRef.current;
      if (s && typeof s.sendContextualUpdate === "function") {
        try {
          s.sendContextualUpdate(
            `The member hand-edited the plan card. It now reads: ${brief}. Work from this plan; acknowledge the change in a few words at your next turn — do not restate the whole plan.`);
        } catch (e) { /* socket closing */ }
      }
    }, 900);
    return () => clearTimeout(t);
  }, [cfg, inputs, phase]);

  const changeMic = async (deviceId) => {
    const s = sessionRef.current;
    if (s && typeof s.changeInputDevice === "function") {
      try {
        await s.changeInputDevice(deviceId ? { inputDeviceId: deviceId } : {});
      } catch (e) {
        log.error("mic switch failed", { error: e });
      }
    }
  };

  const switchToTyping = async () => { await endVoice(); setPhase("typing"); };

  const statusText =
    phase === "starting" ? "Connecting…"
      : speaking ? "Agent speaking — jump in any time"
      : thinking ? "Thinking…" : "Listening…";

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 80, background: "rgba(6,7,10,0.7)",
               display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#12141c", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "18px",
                 padding: "30px 32px", maxWidth: "560px", width: "100%",
                 boxShadow: "0 24px 70px -14px rgba(0,0,0,0.85)", color: "#f1f2f4" }}
      >
        {phase === "sent" ? (
          <div style={{ textAlign: "center", padding: "18px 0" }}>
            <div style={{ fontSize: "34px", marginBottom: "14px" }}>✓</div>
            <div style={{ fontSize: "19px", fontWeight: 600, marginBottom: "8px" }}>Request sent</div>
            <div style={{ fontSize: "14.5px", lineHeight: 1.6, color: "#b6b9c0", marginBottom: "22px" }}>
              The Glonet team has your host request — including your pricing plan — and will
              be in touch by email.
            </div>
            <button
              onClick={onClose}
              style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, cursor: "pointer",
                       border: "none", padding: "12px 26px", borderRadius: "999px" }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.22em", color: ACCENT,
                          textTransform: "uppercase", marginBottom: "10px" }}>
              Request host access
            </div>

            <PlanEditor cfg={cfg} setCfg={setCfg} calc={calc} inputs={inputs}
                        disabled={phase === "sending"} />

            {(phase === "starting" || phase === "live") && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <span style={{
                    flex: "none", width: 40, height: 40, borderRadius: "50%",
                    background: phase === "live" ? ACCENT : "rgba(255,255,255,0.08)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    animation: phase === "live" ? "hl-breathe 1.8s infinite" : "none",
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                         stroke={phase === "live" ? "#0c0d10" : "#f1f2f4"} strokeWidth="1.8" strokeLinecap="round">
                      <rect x="9" y="3" width="6" height="11" rx="3" />
                      <path d="M5 11 a7 7 0 0 0 14 0" />
                      <line x1="12" y1="18" x2="12" y2="21" />
                    </svg>
                  </span>
                  <div style={{ fontSize: "14px", color: "#dfe1e5" }}>{statusText}</div>
                </div>
                <div style={{ fontSize: "12.5px", lineHeight: 1.6, color: "#7c818b", marginTop: "10px" }}>
                  {planTouched
                    ? "The agent will check the plan above is what you meant, answer any questions (what a pre-meeting is, how the split works), and take down your audience story."
                    : "The agent will help you sketch the plan above — it fills in as you talk, and you can edit it by hand any time — then take down your audience story."}
                </div>
                <MicSelector live={phase === "live"} silenceSecs={micSilenceSecs}
                             onDeviceChange={changeMic} holdOff={speaking || thinking} heardTs={heardTick} />
              </>
            )}

            {(phase === "typing" || phase === "error" || phase === "sending") && (
              <>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  placeholder="Tell us about your audience — who they are, how many, and the nexus you want to run…"
                  style={{ width: "100%", resize: "vertical", fontSize: "14px", lineHeight: 1.55, color: "#f1f2f4",
                           background: "#0c0d10", border: "1px solid rgba(255,255,255,0.14)", borderRadius: "10px",
                           padding: "12px 14px", outline: "none", boxSizing: "border-box" }}
                />
                {errMsg && <div style={{ fontSize: "13px", color: "#e88", marginTop: "10px" }}>{errMsg}</div>}
              </>
            )}

            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "18px" }}>
              {(phase === "starting" || phase === "live") && (
                <button onClick={switchToTyping}
                        style={{ fontSize: "12.5px", color: "#7c818b", background: "none", border: "none",
                                 cursor: "pointer", textDecoration: "underline", padding: 0 }}>
                  prefer to type?
                </button>
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: "12px" }}>
                <button
                  onClick={onClose}
                  style={{ fontSize: "14px", color: "#b6b9c0", background: "transparent", cursor: "pointer",
                           border: "1px solid rgba(255,255,255,0.18)", padding: "11px 20px", borderRadius: "999px" }}
                >
                  Cancel
                </button>
                {(phase === "typing" || phase === "error" || phase === "sending") && (
                  <button
                    onClick={() => submit(note, "text", false, "")}
                    disabled={phase === "sending" || !note.trim()}
                    style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, cursor: "pointer",
                             border: "none", padding: "11px 24px", borderRadius: "999px",
                             opacity: phase === "sending" || !note.trim() ? 0.55 : 1 }}
                  >
                    {phase === "sending" ? "Sending…" : "Send request"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---- Compact editable plan card — the same planCfg state as the page
   calculator, so page and dialog can never disagree. The agent writes here
   via set_plan; the member can type/toggle directly. */
function PlanEditor({ cfg, setCfg, calc, inputs, disabled }) {
  const { model, price, members, wc, convos } = cfg;
  const setPart = (k) => (v) => setCfg((prev) => ({ ...prev, [k]: v }));
  const num = (set, min, max) => (e) => {
    const n = Number(e.target.value);
    set(Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : min);
  };
  const field = { fontFamily: MONO, fontSize: "12.5px", color: "#f1f2f4", background: "#0c0d10",
                  border: "1px solid rgba(255,255,255,0.18)", borderRadius: "8px",
                  padding: "6px 8px", width: "84px" };
  const lbl = { fontSize: "11.5px", color: "#7c818b", display: "block", marginBottom: "4px" };
  const pill = (on) => ({ fontSize: "11.5px", cursor: disabled ? "default" : "pointer",
                          padding: "5px 10px", borderRadius: "999px",
                          color: on ? "#0c0d10" : "#b6b9c0", background: on ? ACCENT : "transparent",
                          border: on ? "1px solid transparent" : "1px solid rgba(255,255,255,0.18)",
                          opacity: disabled ? 0.6 : 1 });

  let result;
  if (model === "one_time") {
    result = (
      <span>
        You keep <b style={{ color: "#dfe1e5" }}>{100 - inputs.one_time_platform_pct}%</b> of every ticket
        {calc.p < 3 && <span style={{ color: "#e0a35e" }}> — price tickets at $3+ (card fees eat ${calc.fees.toFixed(2)})</span>}
      </span>
    );
  } else if (calc.viable) {
    result = (
      <span>
        You keep <b style={{ color: "#dfe1e5" }}>{calc.hostPct}%</b> — about{" "}
        <b style={{ color: "#dfe1e5" }}>${(calc.hostMonthly || 0).toLocaleString()}/mo</b> at {Number(members).toLocaleString()} members
      </span>
    );
  } else {
    result = (
      <span style={{ color: "#e0a35e" }}>
        This price doesn&apos;t work — minimum <b>${calc.minPrice}/mo</b> with these features
      </span>
    );
  }

  return (
    <div style={{ marginBottom: "16px", padding: "12px 14px", borderRadius: "10px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
      <div style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.18em", color: "#6b6f78",
                    marginBottom: "10px" }}>YOUR PLAN — EDIT ANY TIME</div>
      <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <span style={lbl}>Model</span>
          <div style={{ display: "flex", gap: "6px" }}>
            <button style={pill(model === "subscription")} disabled={disabled}
                    onClick={() => setPart("model")("subscription")}>Subscription</button>
            <button style={pill(model === "one_time")} disabled={disabled}
                    onClick={() => setPart("model")("one_time")}>One-time</button>
          </div>
        </div>
        <div>
          <span style={lbl}>{model === "subscription" ? "$/member/mo" : "Ticket $"}</span>
          <input type="number" min="1" max="5000" value={price} disabled={disabled}
                 onChange={num(setPart("price"), 1, 5000)} style={field} />
        </div>
        <div>
          <span style={lbl}>Members</span>
          <input type="number" min="10" max="1000000" value={members} disabled={disabled}
                 onChange={num(setPart("members"), 10, 1000000)} style={field} />
        </div>
        <div>
          <span style={lbl}>Water Cooler</span>
          <button style={pill(wc)} disabled={disabled} onClick={() => setPart("wc")(!wc)}>{wc ? "On" : "Off"}</button>
        </div>
        <div>
          <span style={lbl}>Pre-meetings</span>
          <button style={pill(convos)} disabled={disabled} onClick={() => setPart("convos")(!convos)}>{convos ? "On" : "Off"}</button>
        </div>
      </div>
      <div style={{ fontSize: "13px", lineHeight: 1.5, color: "#9a9ea7", marginTop: "10px",
                    paddingTop: "10px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        {result}
        <div style={{ fontSize: "11.5px", color: "#6b6f78", marginTop: "4px" }}>
          Price &amp; features set the split — members only scale your earnings.
        </div>
      </div>
    </div>
  );
}
